import { pushAPI } from '../services/api';

/* ─────────────────────────────────────────────────────────────────────────────
   Background push notifications — reaches a user even with the tab/app
   closed, unlike the Socket.io-based popup+buzzer (PassengerNotificationContext),
   which only works while a page is open. Complementary: this doesn't replace
   that, it's a second delivery channel off the same backend event.

   Deliberately silent/no-op on any unsupported or denied state — this is a
   nice-to-have layered on top of an already-working in-app system, never
   something that should surface an error to the user or block anything.
───────────────────────────────────────────────────────────────────────────── */

// applicationServerKey must be a Uint8Array, but the backend hands back a
// URL-safe base64 string (the standard VAPID key encoding) — this is the
// standard conversion recipe from the Web Push spec.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

let attempted = false; // one attempt per page load is enough — avoid re-prompting on every notification-context remount

export const ensurePushSubscription = async () => {
  if (attempted) return;
  attempted = true;

  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
    if (Notification.permission === 'denied') return;

    const registration = await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      // requestPermission() triggers the native browser prompt on first
      // call; a no-op if already granted. Only proceeds to subscribe on
      // explicit "granted" — "default" (dismissed) and "denied" both stop here.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const res = await pushAPI.getVapidPublicKey();
      const publicKey = res.data?.data?.publicKey;
      if (!publicKey) return; // backend push not configured yet (VAPID env vars unset) — degrade silently

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await pushAPI.subscribe(subscription.toJSON());
  } catch {
    // Silent — push is a bonus channel, never worth surfacing an error over.
  }
};
